import { AppBox, AppTypography, TextWithToolTip } from 'components'
import { useMembershipHelper, useSubAccountsAndDiscounts, useSubAccountStatusUpdateStatus } from 'hooks'
import { useRef } from 'react'
import { AccountApi, AddSubAccountBackToBillingPayload, SubAccountViewModel } from 'sdk'
import {
  ManageSubAccountDialog,
  ManageSubAccountDialogRefType,
} from '../manage-sub-account-dialog/ManageSubAccountDialog'
import {
  RemoveSubAccountDialog,
  RemoveSubAccountDialogRefType,
} from '../remove-sub-account-dialog/RemoveSubAccountDialog'
import { SubAccountsTable } from '../sub-accounts-table/SubAccountsTable'
import {
  AddUserBackToBillingDialog,
  AddUserBackToBillingDialogRefType,
} from '../add-user-back-to-billing-dialog/AddUserBackToDialog'
import { ApiQueryKeys, SubAccountBillingStatus } from 'globals/constants'
import { useMutation } from '@tanstack/react-query'
import { enqueueSnackbar } from 'notistack'

export const ManageUserCard = () => {
  const accountApi = new AccountApi()
  const { allSubAccounts, isGettingAccounts, getSubAccounts, resendInvitation, isInviting } =
    useSubAccountsAndDiscounts({})
  const manageDialogRef = useRef<ManageSubAccountDialogRefType>({})
  const removeDialgRef = useRef<RemoveSubAccountDialogRefType>({})
  const addUserBackDialogRef = useRef<AddUserBackToBillingDialogRefType>({})

  const { mutate: addBack, isPending: isAdding } = useMutation({
    mutationKey: [ApiQueryKeys.account.addSubAccountBack],
    mutationFn: (payload: AddSubAccountBackToBillingPayload) => accountApi.addSubAccountBackToBilling(payload),
    onError: (err) => enqueueSnackbar(err.message, { variant: 'error' }),
    onSuccess: () => {
      enqueueSnackbar('User added back to billing', { variant: 'success' })
      getSubAccounts()
    },
  })

  const openManageDialog = (subAccount: SubAccountViewModel) => {
    manageDialogRef.current.openDialog!(subAccount)
  }
  const { isUpdating, updateSubAccountStatus } = useSubAccountStatusUpdateStatus()
  const { CurrentMembership } = useMembershipHelper()

  const handleRemoveFromPlanClick = (subAccount: SubAccountViewModel) => {
    manageDialogRef.current.closeDialog!()
    removeDialgRef.current.openDialog!(subAccount)
  }
  const handleAddUserBackClick = (user: SubAccountViewModel) => {
    if (user.BillingStatus === SubAccountBillingStatus.Soon_To_Be_Removed) {
      addBack({ SubAccountId: user.Id, Amount: 0 })
    } else {
      addUserBackDialogRef.current.openDialog!(user)
    }
  }
  return (
    <>
      <AppBox border={2} borderColor={'grey.200'} borderRadius={6} padding={2} backgroundColor="white">
        <AppBox display={'flex'} flexDirection={'column'} gap={2}>
          <AppBox paddingBottom={1} borderBottom={1} borderColor={'grey.100'}>
            <TextWithToolTip tooltipText="Disable or remove each of the users that you have added to your plan’s subscription.">
              <AppTypography color={'grey.900'} fontSize={16} fontWeight={700}>
                Manage Users
              </AppTypography>
            </TextWithToolTip>
          </AppBox>
          <AppBox paddingTop={2}>
            <SubAccountsTable
              data={allSubAccounts ?? []}
              isLoading={isGettingAccounts || isInviting || isUpdating || isAdding}
              onManageClick={openManageDialog}
              onResendInvitationClick={resendInvitation}
              onAddUserBackToBilling={handleAddUserBackClick}
            />
          </AppBox>
        </AppBox>
      </AppBox>
      <ManageSubAccountDialog
        ref={manageDialogRef}
        onStatusChange={(_status, subAccountId) => {
          updateSubAccountStatus({ Status: _status, SubAccountId: subAccountId })
            .then((x) => getSubAccounts())
            .catch(() => {})
        }}
        onRemoveFromPlan={(subAccount) => handleRemoveFromPlanClick(subAccount)}
      />
      <RemoveSubAccountDialog
        ref={removeDialgRef}
        onRemoveSuccess={() => getSubAccounts()}
        onStatusChange={(_status, subAccountId) => {
          updateSubAccountStatus({ Status: _status, SubAccountId: subAccountId })
            .then((x) => getSubAccounts())
            .catch(() => {})
        }}
      />
      {!!CurrentMembership() && (
        <AddUserBackToBillingDialog ref={addUserBackDialogRef} onAddSucess={() => getSubAccounts()} />
      )}
    </>
  )
}
